import { useEffect, useMemo, useState } from 'react';
import {
  Box,
  Button,
  FormControlLabel,
  MenuItem,
  Stack,
  Switch,
  TextField,
  Typography,
} from '@mui/material';
import { ErrorAlert } from '../../components/ErrorAlert';
import UploadFileIcon from '@mui/icons-material/UploadFile';
import { Controller, useFormContext } from 'react-hook-form';
import { useQueryClient } from '@tanstack/react-query';
import { z } from 'zod';
import { EntityFormDialog } from '../../components/dialogs/EntityFormDialog';
import {
  useCreateOperator,
  useEditOperator,
  useOperatorBranding,
  useOperatorLogoUrl,
} from '../../api/queries';
import type { Operator } from '../../api/types';
import { COMMON_TIMEZONES, timeZoneLabel } from '../../utils/timezones';
import { apiBaseUrl } from '../../auth/config';
import { useAccessToken } from '../../auth/useAccessToken';

const MAX_LOGO_BYTES = 256 * 1024;
const ALLOWED_MIMES = ['image/png', 'image/jpeg', 'image/svg+xml'];

const schema = z.object({
  code: z.string()
    .trim()
    .min(2, 'At least 2 characters')
    .max(20, 'At most 20 characters')
    .regex(/^[A-Za-z][A-Za-z0-9_-]*$/, 'Letters, digits, _ and - only; must start with a letter'),
  displayName:   z.string().trim().min(1, 'Required').max(100),
  legalName:     z.string().trim().min(1, 'Required').max(200),
  country:       z.string().trim().length(2, 'ISO 3166-1 alpha-2 (e.g. US)').optional()
                  .or(z.literal('').transform(() => undefined)),
  stateProvince: z.string().trim().max(40).optional()
                  .or(z.literal('').transform(() => undefined)),
  timeZone:      z.string().min(1, 'Required'),
  dedicated:     z.boolean(),
});

export type OperatorFormValues = z.infer<typeof schema>;

export type OperatorFormDialogProps = {
  open: boolean;
  mode: 'create' | 'edit';
  operator?: Operator;
  onClose: () => void;
};

export function OperatorFormDialog({ open, mode, operator, onClose }: OperatorFormDialogProps) {
  const create = useCreateOperator();
  const edit = useEditOperator(operator?.id ?? '');
  const getToken = useAccessToken();
  const qc = useQueryClient();

  const [logoFile, setLogoFile] = useState<File | null>(null);
  const [logoError, setLogoError] = useState<string | null>(null);

  // Clear any staged pick / error each time the dialog (re)opens, so a logo
  // selected in a previous session doesn't leak into the next operator edit.
  useEffect(() => {
    if (open) {
      setLogoFile(null);
      setLogoError(null);
    }
  }, [open, operator?.id]);

  const initial: OperatorFormValues = mode === 'edit' && operator
    ? {
        code:          operator.code,
        displayName:   operator.displayName,
        legalName:     operator.legalName,
        country:       operator.country ?? 'US',
        stateProvince: operator.stateProvince ?? '',
        timeZone:      operator.timeZone,
        dedicated:     operator.dedicated,
      }
    : {
        code:          '',
        displayName:   '',
        legalName:     '',
        country:       'US',
        stateProvince: '',
        timeZone:      'America/New_York',
        dedicated:     false,
      };

  // Stage the logo selection inside the form; uploaded after the
  // operator id is known (POST returns it for create; for edit we have
  // it). The branding page is still the dedicated home for colors +
  // logo replacement; this is the inline shortcut so an admin can do
  // the whole onboarding in one screen.
  const uploadLogoIfNeeded = async (operatorId: string) => {
    if (!logoFile || !operatorId) return;
    setLogoError(null);
    if (logoFile.size > MAX_LOGO_BYTES) {
      setLogoError(`Logo too large (max ${MAX_LOGO_BYTES / 1024} KB).`);
      throw new Error('logo size');
    }
    if (!ALLOWED_MIMES.includes(logoFile.type)) {
      setLogoError('Logo must be PNG, JPEG, or SVG.');
      throw new Error('logo type');
    }
    const token = await getToken();
    const fd = new FormData();
    fd.append('file', logoFile);
    const res = await fetch(`${apiBaseUrl}/api/v1/operators/${operatorId}/logo`, {
      method: 'PUT',
      headers: token ? { Authorization: `Bearer ${token}` } : undefined,
      body: fd,
    });
    if (!res.ok) {
      setLogoError(`Logo upload failed: ${res.status} ${res.statusText}`);
      throw new Error('logo upload');
    }
    // Refetch branding (has_logo flips to true on first upload) AND the
    // authenticated logo blob, so the saved logo shows everywhere without a
    // manual refresh. Previously neither was invalidated, so the just-saved
    // logo only appeared after a hard reload.
    qc.invalidateQueries({ queryKey: ['operators', operatorId, 'branding'] });
    qc.invalidateQueries({ queryKey: ['operators', operatorId, 'logo-blob'] });
  };

  const submit = async (values: OperatorFormValues) => {
    if (mode === 'edit') {
      await edit.mutateAsync({
        displayName:   values.displayName,
        legalName:     values.legalName,
        country:       values.country ?? null,
        stateProvince: values.stateProvince || null,
        timeZone:      values.timeZone,
        dedicated:     values.dedicated,
      });
      if (operator?.id) await uploadLogoIfNeeded(operator.id);
    } else {
      const created = await create.mutateAsync({
        code:          values.code,
        displayName:   values.displayName,
        legalName:     values.legalName,
        country:       values.country ?? null,
        stateProvince: values.stateProvince || null,
        timeZone:      values.timeZone,
        dedicated:     values.dedicated,
      });
      await uploadLogoIfNeeded(created.operator_id);
    }
  };

  return (
    <EntityFormDialog<OperatorFormValues>
      open={open}
      mode={mode}
      title={mode === 'create' ? 'Create operator' : 'Edit operator'}
      schema={schema}
      initialValues={initial}
      onSubmit={submit}
      onClose={onClose}
    >
      <Fields mode={mode} />
      <LogoPicker
        operatorId={operator?.id ?? null}
        logoFile={logoFile}
        onPick={(f) => { setLogoFile(f); setLogoError(null); }}
        error={logoError}
      />
    </EntityFormDialog>
  );
}

function LogoPicker({
  operatorId, logoFile, onPick, error,
}: {
  operatorId: string | null;
  logoFile: File | null;
  onPick: (f: File | null) => void;
  error: string | null;
}) {
  // Gate the existing-logo preview on `has_logo` so we never issue a GET that
  // 404s for an operator that hasn't uploaded one yet. The logo is fetched
  // WITH the bearer token as an object-URL (a plain <img> can't auth → 401);
  // the just-picked-file preview below takes precedence for the unsaved case.
  const branding = useOperatorBranding(operatorId);
  const existingLogo = useOperatorLogoUrl(operatorId, branding.data?.has_logo);

  // Memoize the blob URL so it's created once per pick (not every render) and
  // revoked when the pick changes / the picker unmounts — fixes the leak.
  const pendingUrl = useMemo(
    () => (logoFile ? URL.createObjectURL(logoFile) : null),
    [logoFile],
  );
  useEffect(() => {
    return () => { if (pendingUrl) URL.revokeObjectURL(pendingUrl); };
  }, [pendingUrl]);

  return (
    <Stack spacing={1} sx={{ pt: 1 }}>
      <Typography variant="overline" color="text.secondary">Operator logo</Typography>
      <Stack direction="row" spacing={2} alignItems="center">
        <Box sx={{
          width: 96, height: 64, border: 1, borderColor: 'divider',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          bgcolor: 'background.default', borderRadius: 1, overflow: 'hidden',
        }}>
          {pendingUrl ? (
            <Box component="img" key="pending" src={pendingUrl}
                 alt="Pending logo" sx={{ maxWidth: '100%', maxHeight: '100%' }} />
          ) : existingLogo ? (
            <Box component="img" key="existing" src={existingLogo} alt="Operator logo"
                 sx={{ maxWidth: '100%', maxHeight: '100%' }} />
          ) : (
            <Typography variant="caption" color="text.disabled">No logo</Typography>
          )}
        </Box>
        <Stack spacing={0.5}>
          <Button component="label" variant="outlined" size="small" startIcon={<UploadFileIcon />}>
            {logoFile ? 'Change file' : 'Choose file'}
            <input type="file" accept="image/png,image/jpeg,image/svg+xml" hidden
              onChange={(e) => onPick(e.target.files?.[0] ?? null)} />
          </Button>
          <Typography variant="caption" color="text.secondary">
            PNG / JPEG / SVG, ≤ 256 KB. {logoFile ? `Selected: ${logoFile.name}` : 'Uploads on save.'}
          </Typography>
          {logoFile && (
            <Button onClick={() => onPick(null)} size="small" color="inherit">Clear selection</Button>
          )}
        </Stack>
      </Stack>
      {error && <ErrorAlert sx={{ mt: 1 }}>{error}</ErrorAlert>}
    </Stack>
  );
}

function Fields({ mode }: { mode: 'create' | 'edit' }) {
  const { register, control, formState: { errors } } = useFormContext<OperatorFormValues>();

  return (
    <>
      <TextField
        label="Code"
        size="small"
        disabled={mode === 'edit'}
        helperText={mode === 'edit'
          ? 'Code is immutable.'
          : (errors.code?.message ?? 'Letters, digits, _ and -; must start with a letter.')}
        error={!!errors.code}
        fullWidth
        autoFocus={mode === 'create'}
        {...register('code')}
      />
      <TextField
        label="Display name"
        size="small"
        helperText={errors.displayName?.message}
        error={!!errors.displayName}
        fullWidth
        {...register('displayName')}
      />
      <TextField
        label="Legal name"
        size="small"
        helperText={errors.legalName?.message}
        error={!!errors.legalName}
        fullWidth
        {...register('legalName')}
      />
      <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
        <TextField
          label="Country"
          size="small"
          helperText={errors.country?.message ?? 'ISO 3166-1 alpha-2 (US, CA…)'}
          error={!!errors.country}
          sx={{ flex: 1 }}
          {...register('country')}
        />
        <TextField
          label="State / province"
          size="small"
          helperText={errors.stateProvince?.message}
          error={!!errors.stateProvince}
          sx={{ flex: 1 }}
          {...register('stateProvince')}
        />
      </Stack>

      <Controller
        name="timeZone"
        control={control}
        render={({ field, fieldState }) => (
          <TextField
            select
            label="Time zone"
            size="small"
            error={!!fieldState.error}
            helperText={fieldState.error?.message}
            fullWidth
            {...field}
          >
            {COMMON_TIMEZONES.map((tz) => (
              <MenuItem key={tz} value={tz}>{timeZoneLabel(tz)}</MenuItem>
            ))}
          </TextField>
        )}
      />

      <Controller
        name="dedicated"
        control={control}
        render={({ field }) => (
          <FormControlLabel
            control={<Switch checked={field.value} onChange={(_, v) => field.onChange(v)} />}
            label="Dedicated tenant (own infrastructure)"
          />
        )}
      />
    </>
  );
}
